import { useMemo, type ReactNode } from 'react';

import { ItemDialogs } from './ItemDialogs';
import { useGetOutstandingItemsQuery } from './outstanding.api';
import type { OutstandingItem } from './outstanding.schema';
import type { OutstandingAction } from './rowActions';
import { useItemDialogs } from './useItemDialogs';

export interface ItemActions {
  /** The outstanding row matching a change or annual review, or undefined when
   *  nothing is outstanding on it. */
  findByChange: (changeInternalId: string) => OutstandingItem | undefined;
  findByAnnualReview: (annualReviewId: string) => OutstandingItem | undefined;
  open: (item: OutstandingItem, action: OutstandingAction) => void;
  /** Render this inside the consuming component so the dialogs mount. */
  dialogs: ReactNode;
  isLoading: boolean;
}

/**
 * Lets any screen act on a document that is still outstanding.
 *
 * The per-company tabs used to print "Sign and pay from the dashboard", which
 * told a director where to go instead of taking them there — they had to leave
 * the company they were looking at, find the same row among every other
 * company's, and act on it there.
 *
 * Rather than reconstruct an `OutstandingItem` from a form or annual review —
 * which would mean inventing pay/sign state the tab endpoints do not return —
 * this looks the real row up in the outstanding feed and hands it to the same
 * dialogs the dashboard uses. So the tab's buttons and the dashboard's are the
 * same buttons acting on the same data, and a row that is NOT outstanding
 * correctly offers no action at all.
 */
export const useItemActions = (): ItemActions => {
  const { data: items = [], isLoading } = useGetOutstandingItemsQuery({});
  const dialogs = useItemDialogs();

  const byChange = useMemo(() => {
    const map = new Map<string, OutstandingItem>();
    for (const item of items) {
      if (item.changeInternalId !== '') map.set(item.changeInternalId, item);
    }
    return map;
  }, [items]);

  const byAnnualReview = useMemo(() => {
    const map = new Map<string, OutstandingItem>();
    for (const item of items) {
      if (item.annualReviewId !== '') map.set(item.annualReviewId, item);
    }
    return map;
  }, [items]);

  return {
    findByChange: (changeInternalId) =>
      changeInternalId === '' ? undefined : byChange.get(changeInternalId),
    findByAnnualReview: (annualReviewId) =>
      annualReviewId === '' ? undefined : byAnnualReview.get(annualReviewId),
    open: dialogs.open,
    isLoading,
    dialogs: <ItemDialogs state={dialogs} />,
  };
};
